import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Trash2, Loader2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

export default function NewsManager({ open, onClose, onSaved, editing }) {
  const isEdit = Boolean(editing);
  const [title, setTitle] = useState(editing?.title || "");
  const [body, setBody] = useState(editing?.body || "");
  const [type, setType] = useState(editing?.type || "primary");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error("Il titolo è obbligatorio");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/news/${editing.id}`, { title, body, type });
        toast.success("News aggiornata");
      } else {
        await api.post("/news", { title, body, type });
        toast.success("News pubblicata");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore di salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit) return;
    if (!window.confirm("Eliminare questa news?")) return;
    setDeleting(true);
    try {
      await api.delete(`/news/${editing.id}`);
      toast.success("News eliminata");
      onSaved();
      onClose();
    } catch (err) {
      toast.error("Errore eliminazione");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="news-manager-dialog"
        className="max-w-lg rounded-3xl border-[#E8E1D9] bg-white"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold text-[#2D2926]">
            {isEdit ? "Modifica News" : "Aggiungi una News"}
          </DialogTitle>
          <DialogDescription className="text-[#5C5855]">
            Tieni aggiornata la famiglia con le ultime novità di casa.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <label className="mb-2 block text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">
              Titolo
            </label>
            <Input
              data-testid="news-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Es. Patente presa a 16 anni!"
              className="rounded-xl border-[#E8E1D9] bg-white"
            />
          </div>
          <div>
            <label className="mb-2 block text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">
              Dettagli (opzionale)
            </label>
            <Textarea
              data-testid="news-body-input"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Aggiungi un breve commento..."
              rows={3}
              className="rounded-xl border-[#E8E1D9] bg-white resize-none"
            />
          </div>
          <div>
            <label className="mb-2 block text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">
              Importanza
            </label>
            <div className="flex gap-2">
              <button
                data-testid="news-type-primary"
                type="button"
                onClick={() => setType("primary")}
                className={`flex-1 rounded-xl border-2 px-4 py-2.5 text-sm font-semibold transition ${
                  type === "primary"
                    ? "border-[#FF7C43] bg-[#FF7C43]/10 text-[#FF7C43]"
                    : "border-[#E8E1D9] bg-white text-[#5C5855] hover:border-[#C87941]"
                }`}
              >
                Importante
              </button>
              <button
                data-testid="news-type-secondary"
                type="button"
                onClick={() => setType("secondary")}
                className={`flex-1 rounded-xl border-2 px-4 py-2.5 text-sm font-semibold transition ${
                  type === "secondary"
                    ? "border-[#C87941] bg-[#C87941]/10 text-[#C87941]"
                    : "border-[#E8E1D9] bg-white text-[#5C5855] hover:border-[#C87941]"
                }`}
              >
                Normale
              </button>
            </div>
          </div>
        </div>

        <DialogFooter className="flex flex-row items-center justify-between gap-2 sm:justify-between">
          {isEdit ? (
            <Button
              data-testid="news-delete-button"
              type="button"
              variant="ghost"
              onClick={handleDelete}
              disabled={deleting}
              className="text-red-600 hover:bg-red-50 hover:text-red-700"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Elimina
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="rounded-full border-[#E8E1D9]"
            >
              Annulla
            </Button>
            <Button
              data-testid="news-save-button"
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="rounded-full bg-[#FF7C43] text-white hover:bg-[#E66A35]"
            >
              {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              {isEdit ? "Salva" : "Pubblica"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
